import React, { useState, useRef, useEffect } from 'react';
import { Copy, Check, Link2 } from 'lucide-react';
import { copyTextToClipboard } from '../../utils/shareUtils';
import { Modal } from './Modal';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  /** Set when Share is clicked: true/false after auto-copy attempt (null before result). */
  autoClipboardResult?: boolean | null;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareUrl,
  autoClipboardResult = null,
}) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen || autoClipboardResult !== true) return;
    setCopied(true);
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [isOpen, autoClipboardResult]);

  const handleCopy = async () => {
    let success = await copyTextToClipboard(shareUrl);
    if (!success && inputRef.current) {
      inputRef.current.select();
      try {
        success = document.execCommand('copy');
      } catch {
        success = false;
      }
    }
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleInputClick = () => {
    inputRef.current?.select();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col gap-4 pt-2">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gold-100">
            <Link2 size={20} className="text-gold-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Share or Save Your Calculations</h3>
        </div>

        <p className="text-sm text-gray-500">
          This link captures your current calculator inputs. Bookmark it to return to these calculations later, or share it with others.
        </p>

        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            readOnly
            value={shareUrl}
            onClick={handleInputClick}
            className="flex-1 min-w-0 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 cursor-text"
          />
          <button
            onClick={handleCopy}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              copied
                ? 'bg-green-600 text-white focus:ring-green-500'
                : 'bg-gold-500 text-navy-900 hover:bg-gold-400 focus:ring-gold-400'
            }`}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <p className="text-xs text-gray-400">
          Note: manually entered one-time payments in the amortization table are also preserved in this link.
        </p>
      </div>
    </Modal>
  );
};
